import React from "react";
import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../components/ui/accordion";
import { ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal } from "../components/Reveal";
import PageHero from "../components/PageHero";
import { JsonLd } from "../components/SEO";

const faqCategories = [
  {
    name: "Working With Dorado9",
    questions: [
      {
        q: "What does Dorado9 do?",
        a: "Dorado9 is a digital marketing and creative agency that helps businesses build their online presence, attract the right audience and generate growth. Our services include SEO, Google Ads, Meta Ads, social media marketing, web design, branding, content marketing, email marketing and lead generation.",
      },
      {
        q: "What digital marketing services does Dorado9 offer?",
        a: "We offer a range of digital marketing services including search engine optimization (SEO), Google Ads, Meta Ads, social media marketing, content marketing, email marketing, lead generation, web design, branding and graphic design.",
      },
      {
        q: "Does Dorado9 work with businesses in different industries?",
        a: "Yes. We work with businesses across different industries and adapt our strategy according to the business model, target audience, competition, location and growth objectives.",
      },
      {
        q: "Can Dorado9 create a complete digital marketing strategy for my business?",
        a: "Yes. We can develop a digital marketing strategy around your business goals, including organic search, paid advertising, social media, content, website optimization and lead generation where appropriate.",
      },
    ],
  },

  {
    name: "SEO",
    questions: [
      {
        q: "What is SEO and how can it help my business?",
        a: "Search engine optimization (SEO) helps improve your website's visibility in search engines such as Google. A strong SEO strategy can help your business attract relevant organic traffic, reach potential customers and generate more enquiries over time.",
      },
      {
        q: "What SEO services does Dorado9 provide?",
        a: "Our SEO services include keyword research, on-page SEO, technical SEO, content optimization, local SEO, Google Business Profile optimization, link building and ongoing website improvements.",
      },
      {
        q: "How long does SEO take to show results?",
        a: "SEO is a long-term strategy and results vary depending on competition, website authority, technical condition, content and the target market. Some improvements can happen relatively quickly, while stronger organic growth generally takes consistent work over several months.",
      },
      {
        q: "Can Dorado9 help my business rank on Google?",
        a: "Yes. We can improve the technical, content and authority aspects of your website to increase its chances of ranking for relevant searches. SEO results depend on many factors, so rankings cannot be guaranteed for specific keywords.",
      },
      {
        q: "Do you provide local SEO services?",
        a: "Yes. Our local SEO services can help businesses improve their visibility for location-based searches through Google Business Profile optimization, local content, location pages, citations and other local search strategies.",
      },
    ],
  },

  {
    name: "Google Ads & Paid Advertising",
    questions: [
      {
        q: "Does Dorado9 provide Google Ads management?",
        a: "Yes. We can help plan, create, manage and optimize Google Ads campaigns based on your business objectives, target audience, location and budget.",
      },
      {
        q: "Do you manage Meta Ads for Facebook and Instagram?",
        a: "Yes. We provide Meta Ads services for businesses looking to reach relevant audiences through Facebook and Instagram advertising campaigns.",
      },
      {
        q: "How much should I spend on Google Ads?",
        a: "There is no universal advertising budget. The right starting budget depends on your industry, location, competition, target keywords, expected cost per lead and campaign objectives. We can help determine a practical starting point.",
      },
      {
        q: "Can paid advertising and SEO be used together?",
        a: "Yes. SEO and paid advertising can complement each other. Google Ads can provide immediate visibility while SEO works toward sustainable organic visibility and traffic over the longer term.",
      },
    ],
  },

  {
    name: "Web Design & Development",
    questions: [
      {
        q: "Does Dorado9 design business websites?",
        a: "Yes. We design modern, responsive websites that are built around your brand, audience and business goals.",
      },
      {
        q: "Can Dorado9 redesign my existing website?",
        a: "Yes. We can redesign an existing website to improve its visual design, structure, user experience, mobile responsiveness, performance and opportunities for conversion.",
      },
      {
        q: "Do you build SEO-friendly websites?",
        a: "Yes. Websites can be structured with SEO fundamentals in mind, including responsive design, page structure, technical considerations, metadata, content organization and performance.",
      },
      {
        q: "Will my website work on mobile devices?",
        a: "Yes. Our websites are designed to provide a responsive experience across desktop, tablet and mobile devices.",
      },
    ],
  },

  {
    name: "Social Media & Content",
    questions: [
      {
        q: "Does Dorado9 provide social media marketing?",
        a: "Yes. Our social media marketing services can include strategy, content planning, creative development, captions, publishing and ongoing platform management.",
      },
      {
        q: "Which social media platforms do you work with?",
        a: "Depending on your audience and objectives, we can develop strategies for platforms such as Instagram, Facebook and LinkedIn, along with other relevant channels.",
      },
      {
        q: "Does Dorado9 create social media content?",
        a: "Yes. We can create social media content including graphics, promotional creatives, captions, campaign concepts and other content aligned with your brand.",
      },
      {
        q: "Do you provide LinkedIn marketing services?",
        a: "Yes. Our LinkedIn marketing services can include company page content, thought leadership, professional brand positioning and strategies focused on awareness, engagement and lead generation.",
      },
    ],
  },

  {
    name: "Branding & Creative Design",
    questions: [
      {
        q: "What branding services does Dorado9 offer?",
        a: "Our branding services can include logo design, visual identity, typography, color systems, brand guidelines, creative direction and marketing design.",
      },
      {
        q: "Does Dorado9 provide logo design?",
        a: "Yes. We create professional logo designs intended to establish a clear and memorable visual identity for businesses and brands.",
      },
      {
        q: "Can you design graphics for digital marketing campaigns?",
        a: "Yes. We create marketing graphics for social media, advertising campaigns, websites, presentations and other digital communication needs.",
      },
    ],
  },

  {
    name: "Lead Generation & Email Marketing",
    questions: [
      {
        q: "How does Dorado9 generate leads for businesses?",
        a: "Lead generation can combine SEO, Google Ads, Meta Ads, landing pages, content marketing, social media and conversion-focused website strategies depending on the business and target audience.",
      },
      {
        q: "Can Dorado9 create landing pages for lead generation?",
        a: "Yes. We can create landing pages designed around specific campaigns, audiences, offers and conversion goals.",
      },
      {
        q: "Does Dorado9 provide email marketing services?",
        a: "Yes. Our email marketing services can include campaign planning, content, audience segmentation and automated workflows designed to nurture prospects and customers.",
      },
    ],
  },

  {
    name: "Location & Global Services",
    questions: [
      {
  q: "Where is Dorado9 located?",
  a: (

    <>

      Rooted in{" "}

      <a

        href="https://maps.app.goo.gl/eu9YABWpRSsbjYPr7"

        target="_blank"

        rel="noopener noreferrer"

        className="text-brand-mint hover:text-white underline underline-offset-4 transition-colors"

      >

        Amritsar

      </a>

      , Punjab, and working far beyond it. Dorado9 is a

      global digital marketing and creative agency serving

      businesses across India and international markets.

    </>

  ),
},

{
  q: "Does Dorado9 work with clients outside India?",
  a: "Yes. Dorado9 provides digital marketing and creative services to businesses beyond India. Our services can be delivered remotely, allowing us to work with businesses in different countries and markets.",
},
{
  q: "Can I work with Dorado9 if my business is in another country?",
  a: "Yes. Our digital marketing, SEO, advertising, web design, branding and content services can be delivered remotely. We can work with businesses internationally and tailor strategies to their target market.",
},
    ],
  },

  {
    name: "Getting Started",
    questions: [
      {
        q: "How can I get started with Dorado9?",
        a: "Get in touch with our team and tell us about your business, goals and current challenges. We can then discuss the services and strategy that may be most relevant to your needs.",
      },
      {
        q: "How do I know which Dorado9 service is right for my business?",
        a: "The right service depends on your goals. For example, SEO may be suitable for long-term organic visibility, while paid advertising can provide faster campaign visibility. We can help identify the most suitable approach based on your objectives.",
      },
      {
        q: "Can Dorado9 work within my existing marketing setup?",
        a: "Yes. We can work with an existing website, advertising setup, social media presence or marketing strategy and identify areas where improvements can be made.",
      },
      {
        q: "How can I contact Dorado9?",
        a: "You can contact Dorado9 through the contact page on our website. Tell us what you're looking to achieve and our team can discuss the next steps with you.",
      },
    ],
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  name: "Dorado9 Frequently Asked Questions",
  url: "https://dorado9.com/faq",
  mainEntity: faqCategories.flatMap((category) =>
    category.questions.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.a,
      },
    }))
  ),
};

export default function FAQ() {
  return (
    <>
      <JsonLd id="faq-page" data={faqSchema} />
      {/* HERO */}
      <PageHero
        eyebrow="Frequently Asked Questions"
        image="https://images.unsplash.com/photo-1556761175-b413da4baf72?w=1920&q=60&auto=format&fit=crop"
        className="pb-52 lg:pb-28"
        lines={[
          "Straight",
          <>
            <span className="font-serif-accent text-brand-purple lowercase">
              answers.
            </span>
          </>,
        ]}
      >
        <p className="mt-8 text-zinc-400 max-w-xl">
          Everything you need to know about working with Dorado9,
          from digital marketing and SEO to web design, advertising
          and our global services.
        </p>
      </PageHero>

      {/* FAQ */}
      <section
        className="bg-brand-bg py-24 lg:py-32 px-6 lg:px-12 border-t border-zinc-900"
        data-testid="faq-section"
      >
        <div className="max-w-[1600px] mx-auto">

          {faqCategories.map((category, categoryIndex) => (
            <div
              key={category.name}
              className="grid lg:grid-cols-12 gap-12 lg:gap-16 mb-24 last:mb-0"
            >

              {/* LEFT */}
              <div className="lg:col-span-4">
                <Reveal>
                  <p className="eyebrow mb-6">
                    {String(categoryIndex + 1).padStart(2, "0")} / FAQ
                  </p>

                  <h2 className="font-display text-3xl lg:text-5xl tracking-tight uppercase leading-[0.95]">
                    {category.name}
                  </h2>

                  <div className="mt-8 w-10 h-px bg-brand-mint/60" />
                </Reveal>
              </div>

              {/* RIGHT */}
              <div className="lg:col-span-8">
                <Reveal delay={0.08}>
                  <Accordion
                    type="single"
                    collapsible
                    className="w-full"
                    data-testid={`faq-accordion-${categoryIndex}`}
                  >
                    {category.questions.map((f, i) => (
                      <AccordionItem
                        key={i}
                        value={`faq-${categoryIndex}-${i}`}
                        className="border-b border-zinc-900"
                      >
                        <AccordionTrigger
                          className="text-left py-6 font-display text-xl lg:text-2xl uppercase text-white hover:text-brand-mint hover:no-underline transition-colors"
                          data-testid={`faq-question-${categoryIndex}-${i}`}
                        >
                          <span className="flex items-center gap-4">
                            <span className="font-serif-accent text-brand-mint text-2xl leading-none shrink-0">
                              {String(i + 1).padStart(2, "0")}
                            </span>

                            <span>
                              {f.q}
                            </span>
                          </span>
                        </AccordionTrigger>

                        <AccordionContent className="text-zinc-400 text-base leading-relaxed pl-11 pr-4 pb-6 max-w-3xl">
                          {f.a}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </Reveal>
              </div>

            </div>
          ))}

        </div>
      </section>

      {/* FAQ CTA */}
      <section className="relative overflow-hidden bg-brand-bg py-28 lg:py-40 px-6 lg:px-12 border-t border-zinc-900">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] rounded-full blur-[180px] opacity-[0.12] pointer-events-none" style={{ background: "radial-gradient(circle, rgba(102,76,221,.9), transparent 68%)" }} />
        <div className="absolute right-[-120px] bottom-[-180px] w-[500px] h-[500px] rounded-full blur-[170px] opacity-[0.05] pointer-events-none" style={{ background: "radial-gradient(circle, rgba(17,245,186,.8), transparent 68%)" }} />

        <div className="relative z-10 max-w-[1450px] mx-auto">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
            <div className="lg:col-span-7">
              <Reveal>
                <p className="eyebrow mb-7 flex items-center gap-3">
                  <span className="w-8 h-px bg-brand-mint" />
                  Still have questions?
                </p>
                <h2 className="font-display text-5xl sm:text-6xl lg:text-[6vw] leading-[0.88] tracking-tighter uppercase text-white">
                  LET'S TALK
                  <br />
                  <span className="font-serif-accent text-brand-mint lowercase">growth.</span>
                </h2>
                <p className="mt-8 max-w-xl text-zinc-400 text-base lg:text-lg leading-8">
                  Didn't find the answer you were looking for? Tell us what you're trying to achieve and we'll help you figure out the right next step.
                </p>
              </Reveal>
            </div>

            <div className="lg:col-span-5 flex lg:justify-end">
              <Reveal delay={0.15}>
                <a href="https://wa.me/919356020762" target="_blank" rel="noreferrer" aria-label="Talk to Dorado9" className="group/cta relative flex h-[190px] w-[190px] sm:h-[220px] sm:w-[220px] items-center justify-center">
                  <div className="pointer-events-none absolute inset-0 rounded-full bg-brand-purple shadow-[0_0_14px_rgba(102,76,221,0.65),0_0_32px_rgba(102,76,221,0.55),0_0_64px_rgba(102,76,221,0.4),0_0_110px_rgba(102,76,221,0.22)] transition-all duration-500 group-hover/cta:shadow-[0_0_18px_rgba(102,76,221,0.75),0_0_40px_rgba(102,76,221,0.65),0_0_75px_rgba(102,76,221,0.5),0_0_130px_rgba(102,76,221,0.3)] group-hover/cta:scale-[1.04]" />
                  <motion.div animate={{ rotate: 360 }} transition={{ duration: 15, repeat: Infinity, ease: "linear" }} className="pointer-events-none absolute inset-0 z-10">
                    <svg viewBox="0 0 200 200" className="h-full w-full">
                      <defs><path id="faqCtaCircle" d="M100,100 m-78,0 a78,78 0 1,1 156,0 a78,78 0 1,1 -156,0" /></defs>
                      <text fill="#111111" style={{ fontSize: "11px", letterSpacing: "4px", fontWeight: 900 }}>
                        <textPath href="#faqCtaCircle" startOffset="0%">HAVE A QUESTION • LET'S TALK • GET IN TOUCH •</textPath>
                      </text>
                    </svg>
                  </motion.div>
                  <div className="relative z-20 flex h-[105px] w-[105px] sm:h-[120px] sm:w-[120px] items-center justify-center rounded-full bg-[#171717] transition-transform duration-500 group-hover/cta:scale-[1.06]">
                    <ArrowUpRight size={48} strokeWidth={4.5} className="text-white transition-transform duration-500 group-hover/cta:translate-x-1 group-hover/cta:-translate-y-1" />
                  </div>
                </a>
              </Reveal>
            </div>
          </div>

        </div>
      </section>
    </>
  );
}